import { useState } from "react";

import { Box, Flex, Button, temporaryButtonErrorCatcher } from "emoreg";
import { spaces } from "emoreg/const";
import { LargeModal } from "emoreg/organisms/modal";
import { AgencyFilter, agencyFilter, GeoFilter } from "hassibot/component/filter/geography";
import { GenericModalInjectedProps } from "hassibot/component/modal";
import { useAppContext } from "hassibot/context";

import {
  addSelectedStatusToAgencies,
  areAllAgenciesSelected,
  groupAndSortAgenciesByArea,
  NO_MUTUALIZED_AREA,
} from "./agency-filter-modal.helpers";
import { BatchSelectorContainer, ClickableText } from "./agency-filter-modal.styles";
import { SelectableAgency } from "./agency-filter-modal.types";
import { AgencyCheckbox } from "./components/agency-checkbox";
import { MutualizedAreaCheckbox } from "./components/mutualized-area-checkbox";

type AgencyFilterModalProps = GenericModalInjectedProps<{ result: GeoFilter[] }> & {
  previouslySelectedAgencies: AgencyFilter[];
  onValidate: (selectedAgencies: AgencyFilter[]) => void;
  isMyAgenciesSelectionDisabled?: boolean;
};

export const AgencyFilterModal = ({
  previouslySelectedAgencies,
  hideModal,
  onValidate,
  isMyAgenciesSelectionDisabled = false,
  ...rest
}: AgencyFilterModalProps) => {
  const [isPosting, setPosting] = useState(false);
  const { agencies: allAgencies, mutualizedAreas, myAgencies } = useAppContext();

  const [agencies, setAgencies] = useState<SelectableAgency[]>(
    addSelectedStatusToAgencies(previouslySelectedAgencies, allAgencies)
  );

  const onSubmit = (): void => {
    setPosting(true);
    const selectedAgencies: AgencyFilter[] = agencies
      .filter(s => s.isSelected)
      .map(s => agencyFilter(s));
    onValidate(selectedAgencies);
  };

  const handleToggleOne = (agency: SelectableAgency): void => {
    setAgencies(prevState =>
      prevState.map(s => (s.uuid === agency.uuid ? { ...s, isSelected: !s.isSelected } : s))
    );
  };

  const handleSelectAll = (): void => {
    setAgencies(prevState => prevState.map(s => ({ ...s, isSelected: true })));
  };

  const handleUnselectAll = (): void => {
    setAgencies(prevState => prevState.map(s => ({ ...s, isSelected: false })));
  };

  const { agenciesByMutualizedAreaUuid, sortedAgenciesEntries, mutualizedAreasUuidNamesMap } =
    groupAndSortAgenciesByArea(agencies, mutualizedAreas);

  const handleToggleMutualizedArea = (mutualizedAreaUuid: string) => {
    const agenciesInArea = agenciesByMutualizedAreaUuid[mutualizedAreaUuid];
    const areAllAgenciesInAreaSelected = areAllAgenciesSelected(agenciesInArea);

    setAgencies(prevAgenciesState =>
      prevAgenciesState.map(s => {
        const isAgencyInArea = agenciesInArea.some(agency => agency.uuid === s.uuid);

        // if the agency is not in the area, return it unchanged
        if (!isAgencyInArea) {
          return s;
        }

        return {
          ...s,
          isSelected: !areAllAgenciesInAreaSelected,
        };
      })
    );
  };

  const myAgenciesUuids = myAgencies.map(agency => agency.uuid);

  return (
    <LargeModal
      {...rest}
      id="agency-modal"
      title="Choix des agences"
      style={{ paddingTop: spaces[8] }}
      footer={
        <Flex alignItems="center" justifyContent="space-between" grow={1}>
          <Button variant="secondary" onClick={hideModal}>
            Annuler
          </Button>
          <Button disabled={isPosting} onClick={temporaryButtonErrorCatcher(() => onSubmit())}>
            Valider
          </Button>
        </Flex>
      }
    >
      <Flex direction="column" gap={8}>
        <BatchSelectorContainer>
          <ClickableText onClick={() => handleSelectAll()}>Tout sélectionner</ClickableText>
          <ClickableText onClick={() => handleUnselectAll()}>Tout désélectionner</ClickableText>
        </BatchSelectorContainer>

        <Flex
          direction="column"
          wrap="wrap"
          overflow="auto"
          minWidth={{ mobile: "80vw", tablet: "unset" }}
          maxHeight={{ mobile: "unset", tablet: "100vh" }}
        >
          {sortedAgenciesEntries.map(([mutualizedAreaUuid, agencies]) =>
            mutualizedAreaUuid === NO_MUTUALIZED_AREA ? (
              <>
                {agencies.map(agency => (
                  <AgencyCheckbox
                    key={agency.uuid}
                    agency={agency}
                    onToggle={handleToggleOne}
                    isDisabled={
                      isMyAgenciesSelectionDisabled && myAgenciesUuids.includes(agency.uuid)
                    }
                  />
                ))}
              </>
            ) : (
              <div key={mutualizedAreaUuid}>
                <MutualizedAreaCheckbox
                  mutualizedAreaUuid={mutualizedAreaUuid}
                  checked={areAllAgenciesSelected(agencies)}
                  label={mutualizedAreasUuidNamesMap[mutualizedAreaUuid]}
                  onToggle={handleToggleMutualizedArea}
                />
                <Box paddingLeft={16}>
                  {agencies.map(agency => (
                    <AgencyCheckbox
                      key={agency.uuid}
                      agency={agency}
                      onToggle={handleToggleOne}
                      isDisabled={
                        isMyAgenciesSelectionDisabled && myAgenciesUuids.includes(agency.uuid)
                      }
                    />
                  ))}
                </Box>
              </div>
            )
          )}
        </Flex>
      </Flex>
    </LargeModal>
  );
};
